import type { AvlAlias } from '@/services/approvedVendorList';
import {
  PalmettoButton,
  PalmettoCheckbox,
  PalmettoComboBox,
  PalmettoIcon,
  PalmettoSelect,
  PalmettoTextInput,
  PalmettoTextarea,
} from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';
import { Controller, useFieldArray, useFormState } from 'react-hook-form';
import type { Control, FieldArrayPath, FieldValues, Path } from 'react-hook-form';

import { isManufacturerListProgramType, ProgramType, ProgramTypeLabels, VendorEquipmentTypes } from 'types';

import { AliasEditor } from './AliasEditor';
import type { AvlFormValues } from './ApprovedVendorItemForm';
import { type FieldSpec, baseFields, equipmentFields } from './avlFieldRegistry';

// Geothermal/waterHeater/generator are manufacturer-level allow-lists that live in the legacy
// appSettings collection, not approvedVendorList — exclude them so they're never selectable here.
const programTypeOptions = Object.values(ProgramType)
  .filter((value) => !isManufacturerListProgramType(value))
  .map((value) => ({ value, label: ProgramTypeLabels[value] }));

const sizeUnitOptions = [
  { value: 'kW', label: 'kW' },
  { value: 'kWh', label: 'kWh' },
  { value: 'watts', label: 'watts' },
];

const addIcon = <PalmettoIcon name="add" size="sm" />;

interface FeocDataRowProps<TFieldValues extends FieldValues = AvlFormValues> {
  index: number;
  control: Control<TFieldValues>;
  remove: (index: number) => void;
  disabled?: boolean;
}

const FeocDataRow = <TFieldValues extends FieldValues = AvlFormValues>({
  index,
  control,
  remove,
  disabled,
}: FeocDataRowProps<TFieldValues>) => {
  const handleRemove = useCallback(() => remove(index), [remove, index]);
  return (
    <div className="flex flex-wrap items-end gap-8 rounded-8 border border-border-default p-8">
      <Controller
        name={`feocData.${index}.vintage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-vintage-${index}`}
            label="Vintage"
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.subSku` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-subSku-${index}`}
            label="Sub-SKU"
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.mlpeDcPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-mlpeDcPercentage-${index}`}
            label="DC % MLPE"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.stringDcPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-stringDcPercentage-${index}`}
            label="DC % String"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.mlpeFeocPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-mlpeFeocPercentage-${index}`}
            label="FEOC % MLPE"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.stringFeocPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-stringFeocPercentage-${index}`}
            label="FEOC % String"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.bessFeocPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-bessFeocPercentage-${index}`}
            label="BESS FEOC %"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <Controller
        name={`feocData.${index}.bessDcPercentage` as Path<TFieldValues>}
        control={control}
        render={({ field }) => (
          <PalmettoTextInput
            id={`feocData-bessDcPercentage-${index}`}
            label="BESS DC %"
            type="number"
            value={field.value === undefined || field.value === null ? '' : String(field.value)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
            }
            onBlur={field.onBlur}
            disabled={disabled}
          />
        )}
      />
      <PalmettoButton
        type="button"
        emphasis="secondary"
        layout="hug"
        aria-label={`Remove vintage row ${index + 1}`}
        onClick={handleRemove}
        disabled={disabled}
      >
        <PalmettoIcon name="trash" size="sm" />
      </PalmettoButton>
    </div>
  );
};

interface FeocDataEditorProps<TFieldValues extends FieldValues = AvlFormValues> {
  control: Control<TFieldValues>;
  disabled?: boolean;
}

const emptyFeocRow = () => ({
  vintage: '',
  subSku: '',
  mlpeDcPercentage: undefined,
  stringDcPercentage: undefined,
  mlpeFeocPercentage: undefined,
  stringFeocPercentage: undefined,
  bessFeocPercentage: undefined,
  bessDcPercentage: undefined,
});

const FeocDataEditor = <TFieldValues extends FieldValues = AvlFormValues>({
  control,
  disabled,
}: FeocDataEditorProps<TFieldValues>) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'feocData' as FieldArrayPath<TFieldValues>,
  });

  // The concrete row shape can't be expressed generically over an arbitrary TFieldValues.
  const handleAdd = useCallback(() => append(emptyFeocRow() as any), [append]);

  return (
    <div className="col-span-2 flex flex-col gap-8">
      <div className="body-bold-300 text-text-primary">Safe Harbor Vintage Data</div>
      {fields.map((field, index) => (
        <FeocDataRow<TFieldValues>
          key={field.id}
          index={index}
          control={control}
          remove={remove}
          disabled={disabled}
        />
      ))}
      <div>
        <PalmettoButton
          type="button"
          emphasis="secondary"
          layout="hug"
          leadingIcon={addIcon}
          onClick={handleAdd}
          disabled={disabled}
        >
          Add Vintage Row
        </PalmettoButton>
      </div>
    </div>
  );
};

interface SizeFieldProps<TFieldValues extends FieldValues = AvlFormValues> {
  control: Control<TFieldValues>;
  disabled?: boolean;
}

const SizeField = <TFieldValues extends FieldValues = AvlFormValues>({
  control,
  disabled,
}: SizeFieldProps<TFieldValues>) => {
  const { errors } = useFormState({ control, name: 'size' as Path<TFieldValues> });
  const sizeError = (errors as Record<string, { message?: string } | undefined>).size?.message;

  return (
    <div className="flex items-end gap-8">
      <div className="flex-auto">
        <Controller
          name={'size.value' as Path<TFieldValues>}
          control={control}
          render={({ field }) => (
            <PalmettoTextInput
              id="size-value"
              label="Size Value"
              type="number"
              value={field.value === undefined || field.value === null ? '' : String(field.value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
              }
              onBlur={field.onBlur}
              disabled={disabled}
              validationMessage={sizeError ? { type: 'error' as const, text: sizeError } : undefined}
            />
          )}
        />
      </div>
      <div className="w-40">
        <Controller
          name={'size.unit' as Path<TFieldValues>}
          control={control}
          render={({ field }) => (
            <PalmettoSelect
              label="Unit"
              items={sizeUnitOptions.map((opt) => ({ ...opt, selected: opt.value === field.value }))}
              onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? 'kWh')}
              disabled={disabled}
            />
          )}
        />
      </div>
    </div>
  );
};

export const lockedHelperText = 'Locked after first save';

/** `allocation` is locked for a different reason than the Tier 2 identity fields above. */
const allocationLockedHelperText = 'Edit windows in the Allocation Schedule section below instead.';

const lockedHelperTextFor = (fieldName: string): string =>
  fieldName === 'allocation' ? allocationLockedHelperText : lockedHelperText;

export interface AvlFieldRendererProps<TFieldValues extends FieldValues = AvlFormValues> {
  spec: FieldSpec;
  control: Control<TFieldValues>;
  disabled?: boolean;
  locked?: boolean;
  aliasError?: string;
}

/**
 * Registry-driven single-field renderer, genericized over the enclosing form's values so it can
 * back both the single-item form (`AvlFormValues`) and the bulk-edit dialog's dynamically-shaped
 * form (whichever eligible fields `computeSharedFieldSpecs` returns for the current selection).
 */
export const AvlFieldRenderer = <TFieldValues extends FieldValues = AvlFormValues>({
  spec,
  control,
  disabled,
  locked,
  aliasError,
}: AvlFieldRendererProps<TFieldValues>) => {
  const name = spec.name as Path<TFieldValues>;
  const isDisabled = disabled || locked;

  if (spec.kind === 'aliases') {
    return (
      <div className="col-span-2">
        <Controller
          name={'aliases' as Path<TFieldValues>}
          control={control}
          render={({ field }) => (
            <AliasEditor
              value={field.value as AvlAlias[]}
              onChange={field.onChange}
              disabled={disabled}
              errorMessage={aliasError}
            />
          )}
        />
      </div>
    );
  }

  if (spec.kind === 'feocData') {
    return <FeocDataEditor<TFieldValues> control={control} disabled={disabled} />;
  }

  if (spec.kind === 'section') {
    return (
      <div className="col-span-2 border-b border-border-default pb-4">
        <span className="body-bold-300 text-text-secondary">{spec.label}</span>
      </div>
    );
  }

  if (spec.kind === 'size') {
    return (
      <div className="col-span-2">
        <SizeField<TFieldValues> control={control} disabled={disabled} />
      </div>
    );
  }

  if (spec.kind === 'multiselect') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PalmettoComboBox
            id={name}
            label={spec.label}
            helperText={spec.helperText}
            items={programTypeOptions.map((opt) => ({
              ...opt,
              selected: (field.value as string[]).includes(opt.value),
            }))}
            onValueChange={(details: { value: string[] }) => field.onChange(details.value)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (spec.kind === 'select') {
    return (
      <Controller
        name={name}
        control={control}
        rules={spec.required ? { required: `${spec.label} is required` } : undefined}
        render={({ field, fieldState }) => (
          <PalmettoSelect
            label={spec.label}
            items={(spec.options ?? []).map((opt) => ({ ...opt, selected: opt.value === field.value }))}
            onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
            disabled={isDisabled}
            helperText={locked ? lockedHelperTextFor(spec.name) : undefined}
            validationMessage={
              fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
            }
          />
        )}
      />
    );
  }

  if (spec.kind === 'checkbox') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PalmettoCheckbox
            id={name}
            label={spec.label}
            checked={!!field.value}
            onCheckedChange={(details: { checked: boolean | 'indeterminate' }) =>
              field.onChange(details.checked === true)
            }
            disabled={isDisabled}
          />
        )}
      />
    );
  }

  if (spec.kind === 'textarea') {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PalmettoTextarea
            id={name}
            label={spec.label}
            value={(field.value as string) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            rows={2}
            disabled={isDisabled}
          />
        )}
      />
    );
  }

  // text and number
  return (
    <Controller
      name={name}
      control={control}
      rules={spec.required ? { required: `${spec.label} is required` } : undefined}
      render={({ field, fieldState }) => (
        <PalmettoTextInput
          id={name}
          label={spec.label}
          type={spec.kind === 'number' ? 'number' : 'text'}
          value={
            spec.kind === 'number'
              ? field.value === undefined || field.value === null
                ? ''
                : String(field.value)
              : ((field.value as string) ?? '')
          }
          onChange={
            spec.kind === 'number'
              ? (e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
              : field.onChange
          }
          onBlur={field.onBlur}
          required={spec.required}
          disabled={isDisabled}
          helperText={locked ? lockedHelperTextFor(spec.name) : undefined}
          validationMessage={
            fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
          }
        />
      )}
    />
  );
};

interface AvlItemFormFieldsProps {
  equipmentType: VendorEquipmentTypes | '';
  control: Control<AvlFormValues>;
  disabled?: boolean;
  /** Field names that are locked (immutable Tier 2 identity fields already populated). */
  lockedFields?: ReadonlySet<string>;
  aliasError?: string;
}

/**
 * Registry-driven field renderer for AVL items. Renders base fields + per-type spec fields
 * for the selected equipment type. Shows a placeholder when no type is selected.
 */
export const AvlItemFormFields = ({
  equipmentType,
  control,
  disabled,
  lockedFields,
  aliasError,
}: AvlItemFormFieldsProps) => {
  const activeFields = useMemo<FieldSpec[]>(() => {
    if (!equipmentType) return [];
    return [...baseFields, ...equipmentFields[equipmentType]];
  }, [equipmentType]);

  if (!equipmentType) {
    return (
      <div className="mb-16 rounded-8 border border-border-default bg-canvas-highlight-default p-16">
        <p className="body-300 text-text-secondary">Select an equipment type</p>
      </div>
    );
  }

  return (
    <div className="mb-16 grid grid-cols-2 gap-x-16 gap-y-8">
      {activeFields.map((spec) => (
        <AvlFieldRenderer
          key={spec.name}
          spec={spec}
          control={control}
          disabled={disabled}
          locked={lockedFields?.has(spec.name)}
          aliasError={spec.kind === 'aliases' ? aliasError : undefined}
        />
      ))}
    </div>
  );
};
